import {Injectable} from '@angular/core';
import {Constants} from "../../constants";
import {environment} from "../../environments/environment";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {Company} from "../../models/company";
import * as moment from "moment/moment";
import {Athlete} from "../../models/athlete";
import {Parent} from "../../models/parent";
import {CompanyDocument} from "../../models/CompanyDocument";
import {Invoice} from "../../models/invoice";
import {CompanyDocumentService} from "../companyDocument/document.service";
import {Season} from "../../models/season";
import Swal from "sweetalert2";
import {Category} from "../../models/category";
import {AthleteSeason} from "../../models/athleteSeason";
import {Observable, Subject} from "rxjs";

export const POS_BUSINESS_CONFIGURATION_ERROR = 'POS_BUSINESS_CONFIGURATION_ERROR';

@Injectable({
    providedIn: 'root'
})
export class AthleteService {

    constructor(public http: HttpClient, public companyDocumentService: CompanyDocumentService, ) {
    }

    getRequestHeaders() {
        return new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': localStorage.getItem(Constants.LOCAL_TOKEN) ?? environment.publicApiToken
        });
    }

    getPublicRequestHeaders() {
        return new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': environment.publicApiToken,
        });
    }

    getFormDataRequestHeaders() {
        return new HttpHeaders({
            Authorization: localStorage.getItem(Constants.LOCAL_TOKEN) ?? environment.publicApiToken
        });
    }

    async getAllAthlete(additionalFilter = {}) {
        let athletes: any = [];
        let response: any;
        let filter = {}
        if (Object.keys(additionalFilter).length > 0) filter = {...filter, ...additionalFilter}

        try {
            response = await this.http.get(environment.apiUrl + '/athletes', {
                headers: this.getRequestHeaders(),
                params: additionalFilter
            }).toPromise()

            if (response.success) {

                response['data'] = [...response.data].map( (athlete: Athlete) => {
                    return this.addSeasonStatusField(athlete);
                } );
            }

        } catch (e) {
            console.error('error getAllAthletes: ', e)
        } finally {
            return response;
        }
    }

    async getAthleteById(id: string) {
        let athlete: Athlete = new Athlete();

        try {
            let response: any = await this.http.get(environment.apiUrl + `/athletes/${id}`, {
                headers: this.getRequestHeaders(),
            }).toPromise()

            if (response.success) {
                athlete = response.data;

                athlete = this.addSeasonStatusField(athlete);
            }

        } catch (e) {
            console.error('error getAllAthletes: ', e)
        } finally {
            return athlete;
        }
    }

    async updateProfilePicture(formData: FormData, athleteId?: string) {

        let response: any;

        try {
            response = await this.http.post(environment.apiUrl + `/athletes/upload`, formData, {
                headers: this.getFormDataRequestHeaders(),
            }).toPromise();

            return response

        } catch (e) {
            console.error('error updateProfilePicture: ', e)
        }

    }

    async updateAthlete(updatedAthlete: Athlete) {
        let response: any;

        try {
            //updatedAthlete.bornDate = moment(updatedAthlete.bornDate).toISOString();
            response = await this.http.put(environment.apiUrl + `/athletes/${updatedAthlete.id}`, updatedAthlete, {headers: this.getRequestHeaders()}).toPromise();

        } catch (e) {
            console.error('error updateAthlete: ', e)
        } finally {
            return response.success;
        }
    }

    async getAthleteParents(athleteId: string) {
        let parents: Parent[] = [];
        let filter = {athleteId: athleteId}

        try {
            let response: any = await this.http.get(environment.apiUrl + '/parents', {
                headers: this.getRequestHeaders(),
                params: filter,
            }).toPromise()

            if (response.success) {
                parents = [...response.data].map((parent: Parent) => {
                    return {
                        ...parent,
                        bornDate: moment(parent.bornDate).format('YYYY-MM-DD')
                    }
                });

            }

        } catch (e) {
            console.error('error getAllAthletes: ', e)
        } finally {
            return parents;
        }
    }

    async getAthleteDocuments(athleteId: string) {
        let documents: CompanyDocument[] = [];
        let response: any;

        try {
            response = await this.http.get(environment.apiUrl + `/athleteCompanyDocuments/${athleteId}`, {
                headers: this.getRequestHeaders(),
            }).toPromise()

            /*if (response.success) {
                documents = response.data//.map( (document: CompanyDocument) => document.AthleteCompanyDocument);
            }*/

        } catch (e) {
            console.error('error getAllAthletes: ', e)
        } finally {
            return response;
        }
    };

    async deleteAthleteDocuments(athleteId: string, documentId: string) {
        let response: any;

        try {
            response = await this.http.delete(environment.apiUrl + `/athleteCompanyDocuments/${athleteId}/${documentId}`, {
                headers: this.getRequestHeaders(),
            }).toPromise()


        } catch (e) {
            console.error('error getAllAthletes: ', e)
        } finally {
            return response;
        }
    };

    async deleteAthlete(athleteId: string){
        let response: any;

        try {
            response = this.http.delete(environment.apiUrl + `/athletes/${athleteId}`, {headers: this.getRequestHeaders()}).toPromise();
        } catch (e) {
            console.error('deleteAthlete error: ', e)
        } finally {
            return response;
        }
    }

    /**
     * Atleti della società per categoria — allineato a wisper-api `GET /athletes`
     * (`category.service` `getAthlete` con `params.categoryId` sui filtri atleta).
     */
    async getAthleteByCategoryId(
        categoryId: string,
        options?: { companyId?: string; seasonId?: string },
    ): Promise<Athlete[]> {
        let athletes: Athlete[] = [];

        const params: Record<string, string> = { categoryId };
        if (options?.companyId) {
            params['companyId'] = options.companyId;
        }
        if (options?.seasonId) {
            params['seasonId'] = options.seasonId;
        }

        try {
            const response: any = await this.http.get(environment.apiUrl + `/athletes`, {
                params,
                headers: this.getRequestHeaders(),
            }).toPromise();

            if (response.success) {
                athletes = response.data;
            }
        } catch (e) {
            console.error('error getAthleteByCategoryId: ', e);
        } finally {
            return athletes;
        }
    }

    async checkAthleteMandatoryDocuments(athleteId: string, companyId: string) {
        const listOfMandatoryDocuments: CompanyDocument[] = (await this.companyDocumentService
            .getCompanyDocuments(companyId, athleteId))
            .filter((document: CompanyDocument) => document.isMandatory);

        let athleteCompanyDocuments: CompanyDocument[] | null = null;
        try {
            const athleteDocumentsResponse = await this.getAthleteDocuments(athleteId);
            athleteCompanyDocuments = athleteDocumentsResponse.data;
        } catch (error) {
            // Gestisci l'errore se necessario o lascia athleteCompanyDocuments come null
        }

        let missingCompanyDocuments: CompanyDocument[] = [];
        if (!athleteCompanyDocuments || athleteCompanyDocuments.length === 0) {
            // Se l'atleta non ha documenti o è null, gli mancano tutti i documenti obbligatori
            missingCompanyDocuments = [...listOfMandatoryDocuments];
        } else {
            listOfMandatoryDocuments.forEach((mandatoryDocument: CompanyDocument) => {
                if (!athleteCompanyDocuments!.some((athleteDocument: CompanyDocument) => mandatoryDocument.id === athleteDocument.id)) {
                    missingCompanyDocuments.push(mandatoryDocument);
                }
            });
        }

        return missingCompanyDocuments;
    }


    async rejectAthlete(athleteId: string) {
        let response: any;

        try {
            response = this.http.post(environment.apiUrl + `/athletes/${athleteId}/reject`,{},{headers: this.getRequestHeaders()}).toPromise();
        } catch (e) {
            console.error('error RejectAthlete: ', e)
        } finally {
            return response;
        }
    }
    async getAthleteSeasons(athleteId: string): Promise<any[]> {
        try {
            const response: any = await this.http.get(environment.apiUrl + `/athletes/${athleteId}/seasons`, {
                params: {'$skip': 0, '$top': 100},
                headers: this.getRequestHeaders(),
            }).toPromise();
            return response?.success ? response.data : [];
        } catch (e) {
            console.error('error getAthleteSeasons: ', e);
            return [];
        }
    }

    /** Paginated athlete list (`GET /athletes`) with enrollments and memberships. Null on failure. */
    async listAthletes(params: Record<string, string | number | boolean>): Promise<{rows: Athlete[], count: number} | null> {
        try {
            const response: any = await this.http.get(environment.apiUrl + '/athletes', {
                params,
                headers: this.getRequestHeaders(),
            }).toPromise();
            if (!response?.success) return null;
            return {rows: response.data.map((item: any) => new Athlete(item)), count: response.count ?? response.data.length};
        } catch (e) {
            console.error('error listAthletes: ', e);
            return null;
        }
    }

    /** Enrollments of the current season, optionally narrowed to a season or category. Null on failure. */
    async getEnrollments(filters: {seasonId?: string, categoryId?: string}): Promise<AthleteSeason[] | null> {
        try {
            const response: any = await this.http.get(environment.apiUrl + '/athletesSeasons', {
                params: {...filters, '$skip': 0, '$top': 1000},
                headers: this.getRequestHeaders(),
            }).toPromise();
            return response?.success ? response.data.map((item: any) => new AthleteSeason(item)) : null;
        } catch (e) {
            console.error('error getEnrollments: ', e);
            return null;
        }
    }

    async addNewAthlete(newAthlete: Athlete) {
        let response: any;
        try {
            response = this.http.post(environment.apiUrl + `/athletes/`,newAthlete,{headers: this.getRequestHeaders()}).toPromise();
        } catch (e) {
            console.error('error NewAthlete: ', e)
        } finally {
            return response;
        }
    }

    async approveAthlete(athleteId: string) {
        let response: any;

        try {
            response = this.http.post(environment.apiUrl + `/athletes/${athleteId}/approve`,{},{ headers: this.getRequestHeaders() }).toPromise();
        } catch (e) {
            console.error('error approveAthlete: ', e)
        } finally {
            return response;
        }
    }

    async getPendingAthletes() {
        let athletes: Athlete[] = [];
        let response: any;

        try {
            response = await this.http.get(environment.apiUrl + '/athletes?includePendingApprovation=1', {
                // response = await this.http.get(environment.apiUrl + '/athletes', {
                headers: this.getRequestHeaders()
            }).toPromise();

            if (response.success) {
                athletes = response.data
                    .map((athlete: Athlete) => {
                        return {...athlete, birthday: moment(athlete.bornDate).format('DD-MM-YYYY')}
                    });
            }
        } catch (e) {
            console.error('error getting pendingCompanies: ', e)
        } finally {
            return athletes;
        }
    }


    addSeasonStatusField(athlete: Athlete) {

        if (athlete.status === 'pendingCompanyApprovation') {
            return {...athlete, seasonStatus: 'pendingCompanyApprovation'}
        } else if (athlete.status === 'active') {
            let currentSeason = athlete.seasons.find( (season: Season) => season.isCurrent);

            if ( athlete.seasons.length === 0 || !currentSeason ) return {...athlete, seasonStatus: 'pendingPayment'}

            if (currentSeason) {
                return {...athlete, seasonStatus: currentSeason.AthleteSeason.status}
            } else {
                return {...athlete, seasonStatus: undefined}
            }
            
        } else {
            return {...athlete, seasonStatus: undefined}
        }

    }

    async approveAthletePaymentRequest(athletePaymentRequestId: string){
        let response: any;

        try {
            response = await this.http.put(environment.apiUrl + `/athletePaymentRequests/${athletePaymentRequestId}/approve`, {},{headers: this.getRequestHeaders()}).toPromise();
        } catch (e) {
            console.error('error approveAthletePaymentRequest: ', e)
        } finally {
            return response.success;
        }
    }

    async rejectAthletePaymentRequest(athletePaymentRequestId: string){
        let response: any;

        try {
            response = await this.http.put(environment.apiUrl + `/athletePaymentRequests/${athletePaymentRequestId}/reject`, {},{headers: this.getRequestHeaders()}).toPromise();

        } catch (e) {
            console.error('error approveAthletePaymentRequest: ', e)
        } finally {
            return response.success;
        }
    }

    async paymentRequestByAthleteId(athleteId: string){
        let response: any;
        try {
            response = await this.http.get(environment.apiUrl + `/athletePaymentRequestsByAthlete/${athleteId}`, {headers: this.getRequestHeaders()}).toPromise();
        } catch (e) {
            console.error('error approveAthletePaymentRequest: ', e)
        } finally {
            return response;
        }
    }

    async regenerateReceipt(athletePaymentRequestId: string){
        let response: any;

        try {
            response = await this.http.put(environment.apiUrl + `/athletePaymentRequests/${athletePaymentRequestId}/receipt`, {},{headers: this.getRequestHeaders()}).toPromise();
        } catch (e) {
            console.error('error approveAthletePaymentRequest: ', e)
        } finally {
            return response.success;
        }
    }

    async deletePaymentRequest(athleteId: string){
        let response: any;

        try {
            response = await this.http.put(environment.apiUrl + `/athletePaymentRequests/${athleteId}/delete`, {},{headers: this.getRequestHeaders()}).toPromise();
        } catch (e) {
            console.error('error approveAthletePaymentRequest: ', e)
        } finally {
            return response;
        }
    }

    async checkExistingAthlete(params: any){
        let response: any;
        try {
            response = await this.http.get(environment.apiUrl + `/checkExistingAthlete`, {
                params,
                headers: this.getRequestHeaders()
            }).toPromise();
        } catch (e) {
            console.error('error approveAthletePaymentRequest: ', e)
        } finally {
            return response;
        }
    }


    async getPaymentStatus(athletePaymentRequestId: string){
        let response: any;
        try {
            response = await this.http.get(environment.apiUrl + `/athletePaymentRequests/${athletePaymentRequestId}/startPaymentPos`, {headers: this.getRequestHeaders()}).toPromise();
        } catch (e) {
            console.error('error getPaymentStatus: ', e)
        } finally {
            return response.data;
        }
    }
    async startPaymentPos(athletePaymentRequestId: string, paymentType: "SINGLE" | "RECURRING"){
        let response: any;
        try {
            response = await this.http.put(environment.apiUrl + `/athletePaymentRequests/${athletePaymentRequestId}/startPaymentPos`, {paymentType},{headers: this.getRequestHeaders()}).toPromise();
            if (this.isPosProviderUnauthorized(response)) {
                return {error: POS_BUSINESS_CONFIGURATION_ERROR};
            }
            return response?.data;
        } catch (e: any) {
            console.error('error startPaymentPos: ', e)
            if (this.isPosProviderUnauthorized(e?.error ?? e)) {
                return {error: POS_BUSINESS_CONFIGURATION_ERROR};
            }
            return undefined;
        }
    }

    private isPosProviderUnauthorized(response: any): boolean {
        return response?.name === 'AxiosError' && response?.status === 401;
    }

    listenStatusPaymentPos(athletePaymentRequestId: string): Observable<any> {
        const statusSubject = new Subject<any>();
        const token = localStorage.getItem(Constants.LOCAL_TOKEN) ?? environment.publicApiToken;
        
        const url = `${environment.apiUrl}/athletePaymentRequests/${athletePaymentRequestId}/realTimeStatus`;
        
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': token
            }
        })
        .then(async (response) => {
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const reader = response.body?.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            
            if (!reader) {
                statusSubject.error(new Error('Response body is not readable'));
                return;
            }
            
            try {
                while (true) {
                    const { done, value } = await reader.read();
                    
                    if (done) {
                        statusSubject.complete();
                        break;
                    }
                    
                    // Decode the chunk and add to buffer
                    buffer += decoder.decode(value, { stream: true });
                    
                    // Process complete lines (ending with \n)
                    const lines = buffer.split('\n');
                    // Keep the last incomplete line in buffer
                    buffer = lines.pop() || '';
                    
                    // Process each complete line
                    for (const line of lines) {
                        if (line.trim()) {
                            try {
                                const status = JSON.parse(line);
                                statusSubject.next(status);
                            } catch (e) {
                                console.error('Error parsing status line:', line, e);
                            }
                        }
                    }
                }
            } catch (error) {
                statusSubject.error(error);
            }
        })
        .catch((error) => {
            console.error('Error in listenStatusPaymentPos:', error);
            statusSubject.error(error);
        });
        
        return statusSubject.asObservable();
    }
}
